import { getAffiliateData } from './utils/getAffiliateData';
import { getLanguageConfig } from './utils/getLanguageConfig';

import {
  FullInfoResponse,
  useApiContext,
  useGetFullInfo,
  useGetUserCountry,
  UserCountryResponse
} from 'react-easyrocket';
import { Config } from './types';
import { SupportedLanguages } from '../../modules/locales/types';
import { useEffect, useState } from 'react';
import { LANGUAGE_CONFIG } from './config';

interface useGetCountryConfigInput {
  config: Config;
  userLanguage: SupportedLanguages;
  isFetchingFullInfo: boolean;
  isFetchingUserCountry: boolean;
  userCountry: UserCountryResponse;
  isAuthenticated: boolean;
  fullInfo: FullInfoResponse;
  isGBR: boolean;
  hasAffiliateLink: boolean;
  isLoadingFullInfo: boolean;
}

/**
 * Хук `useGetCountryConfig` предназначен для получения конфигурации, связанной с языком и страной пользователя.
 * Он использует несколько вспомогательных функций и хуков для сбора необходимых данных, таких как информация о пользователе,
 * его стране, языке и наличии партнерской ссылки. На основе этих данных хук возвращает конфигурацию, которая может быть
 * использована для настройки интерфейса в зависимости от региона и языка пользователя.
 *
 * @param {keyof SupportedLanguages} language - Язык, который будет использоваться по умолчанию, если язык пользователя не определен.
 *
 * @returns {useGetCountryConfigInput} - Объект, содержащий конфигурацию и связанные с ней данные.
 * @property {Config} config - Конфигурация, зависящая от языка и страны пользователя.
 * @property {keyof SupportedLanguages} userLanguage - Язык пользователя, определенный на основе данных из `fullInfo` или переданный в параметрах.
 * @property {boolean} isFetchingFullInfo - Флаг, указывающий, выполняется ли в данный момент запрос на получение полной информации о пользователе.
 * @property {boolean} isFetchingUserCountry - Флаг, указывающий, выполняется ли в данный момент запрос на получение страны пользователя.
 * @property {UserCountryResponse} userCountry - Данные о стране пользователя.
 * @property {boolean} isAuthenticated - Флаг, указывающий, аутентифицирован ли пользователь.
 * @property {FullInfoResponse} fullInfo - Полная информация о пользователе, полученная с помощью хука `useGetFullInfo`.
 * @property {hasAffiliateLink} hasAffiliateLink - информация о том как пользователь пришёл на сайт.
 */

export const useGetCountryConfig = ({
  language
}: {
  language: SupportedLanguages;
}): useGetCountryConfigInput => {
  const { isAuthenticated } = useApiContext();
  const {
    fullInfo,
    isFetching: isFetchingFullInfo,
    isLoading: isLoadingFullInfo
  } = useGetFullInfo();
  const { userCountry, isGBR, isFetching: isFetchingUserCountry } = useGetUserCountry();

  const [languageConfig, setLanguageConfig] = useState({
    language: (fullInfo?.language as keyof SupportedLanguages) || language,
    config: LANGUAGE_CONFIG.get('default')?.config as Config
  });

  const hasAffiliateLink = getAffiliateData();

  useEffect(() => {
    const config = getLanguageConfig({
      isGBR,
      hasAffiliateLink,
      language: (fullInfo?.language as SupportedLanguages) || language,
      isAuthenticated
    });
    setLanguageConfig(config);

    return () => {
      config.clearInterseptors?.();
    };
  }, [fullInfo?.language, isAuthenticated, isGBR, language]);

  return {
    config: languageConfig.config,
    userLanguage: languageConfig.language as SupportedLanguages,
    isFetchingFullInfo,
    isFetchingUserCountry,
    isLoadingFullInfo,
    userCountry,
    isAuthenticated,
    fullInfo,
    isGBR,
    hasAffiliateLink
  };
};
